import { Info, Trash2 } from 'lucide-react'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/admin/coaching/coaching-dashboard/shared/tooltip'
import { DraggableCard } from '@/components/shared/card-board'
import {
  COACHING_PACES,
  type CoachingPace,
  ESTIMATE_TUNING,
} from '@/lib/coaching/estimate-minutes'
import { useCoachingStore } from '../../store'
import type { TrayCard } from '../_lib/auto-place'
import { CardParentage } from '../schedule/schedule-card'
import { EstimateStepper } from '../shared/estimate-stepper'
import { InlineSourceSwitcher } from '../shared/inline-source-switcher'

const TYPE_ACCENT: Record<TrayCard['type'], string> = {
  coding: 'var(--cd-amber)',
  sd: 'var(--cd-purple)',
  behavioral: 'var(--cd-blue)',
  interview: 'var(--cd-green)',
  other: 'var(--cd-gray)',
}

export interface StagingTrayProps {
  /** Opens the card-details modal (LG-360 §1); the tab above owns which card. */
  onOpenDetails?: (cardId: string) => void
}

export function StagingTray(props: StagingTrayProps = {}): ReactNode {
  const { onOpenDetails } = props
  const { curriculum } = useCoachingStore()
  const {
    distributeTray,
    placing,
    removeFromTray,
    switchCardSource,
    togglePlacing,
    tray,
  } = curriculum
  // Optimistic pace: the select is controlled by the server value, which
  // only updates after the mutation + refetch round-trip. While the write is
  // in flight, show the coach's choice instead of snapping back to the old
  // value (which reads as "didn't take" and invites a stale-version retry).
  const [pendingPace, setPendingPace] = useState<CoachingPace | null>(null)
  const shownPace = curriculum.pacePending
    ? (pendingPace ?? curriculum.pace)
    : curriculum.pace
  const distributionDisabled =
    tray.length === 0 || curriculum.bulkActionsReady === false
  const headingId = 'staging-tray-heading'

  return (
    // `xl:min-h-0` completes the chain the row splitter (LG-611) needs, the
    // same way `LibraryCard` carries it: `h-full` alone still leaves the
    // section a flex item at `min-height: auto`, free to grow past the row to
    // fit its own header. That header is tallest exactly when the row is
    // shortest — the column splitter can squeeze the tray to 4% of the width,
    // where the heading, Distribute button and pace select each wrap onto
    // their own line — so without this the tray spills over the divider
    // instead of scrolling its list inside the region.
    <section
      aria-labelledby={headingId}
      className="flex h-full min-w-0 flex-col rounded-xl border border-(--cd-border-3) bg-(--cd-shell) p-4 xl:min-h-0"
    >
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <h2
          id={headingId}
          className="whitespace-nowrap text-[11.5px] font-bold tracking-[0.08em] text-(--cd-text-muted)"
        >
          STAGING TRAY · {tray.length}
        </h2>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              className="ml-auto whitespace-nowrap rounded-full bg-(--cd-purple) px-3 py-0.5 text-[11px] font-semibold text-(--cd-bg) transition-opacity"
              onClick={distributeTray}
              // Disabled, not just dimmed: an empty-tray click would flash
              // "Distributed 0 card(s)" and cancel an in-progress placement.
              disabled={distributionDisabled}
              style={{ opacity: distributionDisabled ? 0.45 : 1 }}
              type="button"
            >
              ✦ Distribute by hours
            </button>
          </TooltipTrigger>
          <TooltipContent>
            {curriculum.bulkActionsReason ?? 'Distribute staged cards by hours'}
          </TooltipContent>
        </Tooltip>
      </div>

      <div className="mb-2.5 flex flex-wrap items-center gap-2">
        <p className="text-[10px] text-(--cd-text-faint)">
          picked but not scheduled — never visible to the client
        </p>
        {curriculum.persisted && curriculum.pace && curriculum.setPace ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <label className="ml-auto flex items-center gap-1 text-[10px] text-(--cd-text-faint)">
                pace
                <select
                  aria-label="Client pace — scales all computed estimates"
                  value={shownPace}
                  disabled={curriculum.pacePending === true}
                  onChange={(event) => {
                    const next = COACHING_PACES.find(
                      (pace) => pace === event.currentTarget.value,
                    )
                    if (!next) return
                    setPendingPace(next)
                    curriculum.setPace?.(next)
                  }}
                  className="cursor-pointer rounded border border-(--cd-border-2) bg-(--cd-bg) px-1 py-0.5 text-[10px] text-(--cd-text-muted) disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {COACHING_PACES.map((pace) => (
                    <option key={pace} value={pace}>
                      {pace}
                    </option>
                  ))}
                </select>
              </label>
            </TooltipTrigger>
            <TooltipContent>{PACE_TOOLTIP}</TooltipContent>
          </Tooltip>
        ) : null}
      </div>
      {curriculum.bulkActionsReady === false && curriculum.bulkActionsReason ? (
        <p className="mb-2 text-[10px] text-(--cd-amber)">
          {curriculum.bulkActionsReason}
        </p>
      ) : null}

      {tray.length === 0 ? (
        <p className="rounded-lg border border-dashed border-(--cd-border-2) px-4 py-4 text-center text-[11px] text-(--cd-text-ghost)">
          Empty — &quot;Tray&quot; any library item, or remove a scheduled card.
        </p>
      ) : (
        <div className="flex max-h-[min(60vh,520px)] flex-col gap-1.5 overflow-y-auto xl:max-h-none xl:min-h-0 xl:flex-1">
          {tray.map((card) => {
            const selected = placing?.id === card.id
            // The source belongs in the accessible name, not only in the mark:
            // a coach can stage both faces of one problem, and without this
            // they announce identically.
            const cardLabel = card.sourceDisplayName
              ? `${card.title} · ${card.sourceDisplayName}`
              : card.title
            const borderColor = selected
              ? 'color-mix(in oklab, var(--cd-green) 60%, transparent)'
              : 'var(--cd-border-2)'

            return (
              <DraggableCard
                key={card.id}
                id={card.id}
                data={{ kind: 'tray', card }}
              >
                <article
                  aria-label={cardLabel}
                  className="flex flex-wrap items-start gap-x-2 gap-y-1.5 rounded-lg border border-l-[3px] bg-(--cd-inset) px-2.5 py-2"
                  style={{
                    borderBottomColor: borderColor,
                    borderLeftColor: TYPE_ACCENT[card.type],
                    borderRightColor: borderColor,
                    borderTopColor: borderColor,
                    boxShadow: selected
                      ? '0 0 0 1px color-mix(in oklab, var(--cd-green) 40%, transparent)'
                      : 'none',
                  }}
                >
                  <span className="flex min-w-0 flex-[1_1_11rem] items-start gap-1">
                    {/* A sibling of the placement button, not a child: the
                        switcher's marks are buttons themselves, and buttons
                        cannot nest. The source still rides on `cardLabel` —
                        otherwise two staged cards for the same problem, one
                        LeetCode and one BCtCI, announce identically. */}
                    <InlineSourceSwitcher
                      sources={card.sources ?? []}
                      currentSourceId={card.sourceId ?? null}
                      fallbackIcon={card.sourceIcon ?? null}
                      onPick={
                        switchCardSource
                          ? (sourceId) =>
                              switchCardSource({ cardId: card.id, sourceId })
                          : undefined
                      }
                    />
                    <button
                      aria-label={
                        selected
                          ? `Cancel placing ${cardLabel}`
                          : `Select ${cardLabel} for manual placement — then choose a green schedule slot`
                      }
                      aria-pressed={selected}
                      className="flex min-w-0 flex-1 cursor-pointer items-start gap-2 text-left"
                      onClick={() => togglePlacing(card.id)}
                      type="button"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block text-[11px] font-semibold text-(--cd-text)">
                          {card.title}
                        </span>
                        <span className="block text-[10px] text-(--cd-text-faint)">
                          {card.meta}
                        </span>
                      </span>
                      {/* No idle hint: the button's aria-label carries the
                          affordance, and the visual cue appears only once a
                          card is armed. */}
                      {selected ? (
                        <span className="mt-0.5 whitespace-nowrap text-[9.5px] font-semibold text-(--cd-green)">
                          click a green slot ↓
                        </span>
                      ) : null}
                    </button>
                  </span>
                  <span className="ml-auto flex shrink-0 items-center gap-1.5">
                    {/* A tray card's body is already the placement control, so
                        details get their own button here rather than the
                        body-click the schedule uses (LG-360 §1). */}
                    {onOpenDetails ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            aria-label={`Open details for ${cardLabel}`}
                            className="cursor-pointer border-0 bg-transparent p-0 text-[10px] text-(--cd-text-ghost) hover:text-(--cd-text)"
                            onClick={(event) => {
                              event.stopPropagation()
                              onOpenDetails(card.id)
                            }}
                            type="button"
                          >
                            <Info size={12} strokeWidth={2} aria-hidden />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>Open details</TooltipContent>
                      </Tooltip>
                    ) : null}
                    {curriculum.persisted &&
                    curriculum.setAssignmentEstimate ? (
                      <TrayEstimateControl
                        key={`${card.id}:${card.version ?? 0}:${card.estimatedMinutes ?? 'unknown'}`}
                        card={card}
                        onSave={curriculum.setAssignmentEstimate}
                      />
                    ) : null}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          aria-label={`Remove ${card.title} from tray`}
                          className="cursor-pointer border-0 bg-transparent p-0 text-[10px] text-(--cd-text-ghost) hover:text-(--cd-red) disabled:cursor-not-allowed disabled:opacity-40"
                          // Disabled during the cancel round-trip: the card
                          // stays visible until the refetch lands, and a second
                          // click would send a stale-version cancel that flashes
                          // a misleading CONFLICT message.
                          disabled={
                            curriculum.removingIds?.includes(card.id) === true
                          }
                          onClick={(event) => {
                            event.stopPropagation()
                            removeFromTray(card.id)
                          }}
                          type="button"
                        >
                          <Trash2 size={12} strokeWidth={2} aria-hidden />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>Remove from tray</TooltipContent>
                    </Tooltip>
                  </span>
                  {/* A full-width third row rather than a child of the title
                      button: both its lines wrap, so it needs the whole card
                      width, and nesting it inside the button would fold its
                      text into that button's accessible name. The tray is
                      where a breadcrumb matters most — `assignContainer`'s
                      Tray destination drops a whole chapter's members into one
                      undifferentiated pile, and the auto destination sends
                      whatever did not fit here too. */}
                  <CardParentage card={card} className="w-full" />
                </article>
              </DraggableCard>
            )
          })}
        </div>
      )}
      {curriculum.persisted && curriculum.trayHasMore ? (
        <button
          type="button"
          aria-label="Load more staged assignments"
          disabled={curriculum.trayLoadingMore || !curriculum.loadMoreTray}
          onClick={curriculum.loadMoreTray}
          className="mx-auto mt-2 rounded-full border border-(--cd-border-2) px-3 py-1 text-[10.5px] font-semibold text-(--cd-text-muted) disabled:cursor-not-allowed disabled:opacity-50"
        >
          {curriculum.trayLoadingMore
            ? 'Loading more staged assignments…'
            : 'Load more staged assignments'}
        </button>
      ) : null}
    </section>
  )
}

const ESTIMATE_MAX = ESTIMATE_TUNING.ceilingMinutes
const ESTIMATE_COMMIT_DELAY_MS = 400

const PACE_TOOLTIP = `How quickly this client works relative to peers — scales every computed estimate (${COACHING_PACES.map(
  (pace) => `${pace} ×${ESTIMATE_TUNING.paceMultipliers[pace].toFixed(1)}`,
).join(' · ')})`

function TrayEstimateControl(props: {
  card: TrayCard
  onSave: (
    cardId: string,
    estimatedMinutes: number | null,
  ) => void | Promise<boolean>
}): ReactNode {
  const { card, onSave } = props
  const committed = card.estimatedMinutes ?? null
  const [pending, setPending] = useState<number | null>(null)
  const timer = useRef<number | null>(null)
  // Latest value a live timer would commit — read by the unmount cleanup so
  // a mid-debounce unmount flushes the coach's clicks instead of dropping
  // them.
  const uncommitted = useRef<number | null>(null)
  const onSaveRef = useRef(onSave)
  onSaveRef.current = onSave
  // Commit trailing-edge so a burst of clicks is one write. On success
  // `pending` stays set — clearing it would snap the display back to the
  // stale committed prop for the whole mutation + refresh round-trip; it
  // clears naturally when the refreshed server value lands, because the
  // call-site remount `key` includes card.version and card.estimatedMinutes.
  // On failure (`onSave` resolves false) that key never changes, so `pending`
  // is cleared here to fall back to the refreshed server value.
  const commit = (value: number) => {
    uncommitted.current = value
    if (timer.current !== null) window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => {
      timer.current = null
      uncommitted.current = null
      void Promise.resolve(onSaveRef.current(card.id, value)).then((saved) => {
        if (saved === false) setPending(null)
      })
    }, ESTIMATE_COMMIT_DELAY_MS)
  }
  // The remount key also changes when a refetch moves the *derived* value
  // (a pace change or ELO refresh landing mid-debounce), so an unmount is
  // not proof the save landed — flush a still-pending commit instead of
  // silently discarding it.
  useEffect(
    () => () => {
      if (timer.current !== null) {
        window.clearTimeout(timer.current)
        if (uncommitted.current !== null) {
          void onSaveRef.current(card.id, uncommitted.current)
        }
      }
    },
    [card.id],
  )
  // Persisted cards always arrive with an effective estimate (the engine
  // falls back to defaultEstimateMinutes), so the fallback here is only a
  // guard for cards built outside that path.
  const value = pending ?? committed ?? ESTIMATE_TUNING.defaultEstimateMinutes
  const reset = () => {
    if (timer.current !== null) window.clearTimeout(timer.current)
    timer.current = null
    uncommitted.current = null
    setPending(null)
    void onSaveRef.current(card.id, null)
  }
  const range =
    card.estimateLow != null && card.estimateHigh != null
      ? `${card.estimateLow}–${card.estimateHigh}m`
      : null

  return (
    <span className="flex shrink-0 items-center gap-1">
      {range ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="whitespace-nowrap text-[10px] text-(--cd-text-faint)">
              {range}
            </span>
          </TooltipTrigger>
          <TooltipContent>
            Expected range from client ELO and pace
          </TooltipContent>
        </Tooltip>
      ) : null}
      <EstimateStepper
        label={`Estimated minutes for ${card.title}`}
        subject={`for ${card.title}`}
        value={value}
        onChange={(next) => {
          setPending(next)
          commit(next)
        }}
        maxMinutes={ESTIMATE_MAX}
        valueColor={
          card.estimateOverridden ? 'var(--cd-amber)' : 'var(--cd-text)'
        }
      />
      {card.estimateOverridden ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={`Reset estimate for ${card.title} to computed`}
              onClick={reset}
              className="cursor-pointer border-0 bg-transparent p-0 text-[10px] text-(--cd-text-ghost) hover:text-(--cd-text)"
            >
              ↺
            </button>
          </TooltipTrigger>
          <TooltipContent>Manual override — reset to computed</TooltipContent>
        </Tooltip>
      ) : null}
    </span>
  )
}
